import React, { useEffect, useRef, useState } from 'react';
import { createChart, CandlestickSeries, LineSeries, createSeriesMarkers } from 'lightweight-charts';
import {
  buildDiCrossMarkers,
  chartTimeFromIso,
  ohlcChartTime,
  regimeDisplay,
  spreadTrendArrow,
} from '../utils/adxChartData';
import './AdxDashboard.css';

const COLOR_ADX      = '#A16D17';
const COLOR_DI_PLUS  = '#1F4E8C';
const COLOR_DI_MINUS = '#C0571F';
const COLOR_SPREAD   = '#4A5672';
const COLOR_LEVEL25  = '#4A5672';

const TF_CONFIG = {
  daily: { label: 'D',  defaultDays: 60, adxTf: 'daily', ohlcInterval: 1440 },
  '4h':  { label: '4H', defaultDays: 14, adxTf: '4h',    ohlcInterval: 60   },
  '1h':  { label: '1H', defaultDays: 3,  adxTf: '1h',    ohlcInterval: 60   },
};

const TIMEFRAMES = ['daily', '4h', '1h'];

function compareChartTime(a, b) {
  if (a == null || b == null) return 0;
  if (typeof a === 'string' || typeof b === 'string') {
    return String(a).localeCompare(String(b));
  }
  return a - b;
}

function dedupSort(arr) {
  const m = new Map();
  arr.forEach(p => {
    if (p.time == null) return;
    m.set(p.time, p);
  });
  return Array.from(m.values()).sort((a, b) => compareChartTime(a.time, b.time));
}

function finiteNum(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function isValidVisibleRange(range) {
  if (!range || range.from == null || range.to == null) return false;
  if (typeof range.from === 'string') {
    const from = String(range.from);
    const to = String(range.to);
    return from.length >= 10 && to.length >= 10 && from < to;
  }
  return Number.isFinite(range.from) && Number.isFinite(range.to) && range.from < range.to;
}

function AdxDashboard() {
  const priceContainerRef = useRef(null);
  const adxContainerRef   = useRef(null);

  const priceChartRef = useRef(null);
  const adxChartRef   = useRef(null);
  const candleRef     = useRef(null);
  const adxLineRef    = useRef(null);
  const diPlusRef     = useRef(null);
  const diMinusRef    = useRef(null);
  const spreadRef     = useRef(null);
  const level25Ref    = useRef(null);
  const seriesMarkersRef = useRef(null);
  const adxRowsRef    = useRef([]);
  const syncBlockRef  = useRef(false);

  const [pair, setPair]                     = useState('');
  const [availablePairs, setAvailablePairs] = useState([]);
  const [timeframe, setTimeframe]           = useState('daily');

  const [priceLoading, setPriceLoading] = useState(true);
  const [adxLoading, setAdxLoading]     = useState(true);
  const [error, setError]               = useState(null);
  const [latest, setLatest]             = useState(null);

  useEffect(() => {
    if (!priceContainerRef.current || !adxContainerRef.current) return;

    const sharedTsOpts = {
      borderColor: '#E3D9C6',
      timeVisible:    true,
      secondsVisible: false,
    };

    const priceChart = createChart(priceContainerRef.current, {
      layout:          { background: { color: '#FFFBF3' }, textColor: '#16244A' },
      grid:            { vertLines: { color: '#EDE3CF' }, horzLines: { color: '#EDE3CF' } },
      crosshair:       { mode: 1 },
      rightPriceScale: { borderColor: '#E3D9C6' },
      timeScale:       sharedTsOpts,
      width:  priceContainerRef.current.clientWidth,
      height: priceContainerRef.current.clientHeight,
    });

    const candleSeries = priceChart.addSeries(CandlestickSeries, {
      upColor: '#1F4E8C', downColor: '#C0571F',
      borderVisible: false,
      wickUpColor: '#1F4E8C', wickDownColor: '#C0571F',
    });

    const adxChart = createChart(adxContainerRef.current, {
      layout:          { background: { color: '#FFFBF3' }, textColor: '#16244A' },
      grid:            { vertLines: { color: '#EDE3CF' }, horzLines: { color: '#EDE3CF' } },
      crosshair:       { mode: 1 },
      rightPriceScale: { borderColor: '#E3D9C6' },
      timeScale:       { ...sharedTsOpts, visible: false },
      width:  adxContainerRef.current.clientWidth,
      height: adxContainerRef.current.clientHeight,
    });

    const adxLine = adxChart.addSeries(LineSeries, {
      color: COLOR_ADX, lineWidth: 2,
      priceLineVisible: false, lastValueVisible: true, title: 'ADX',
    });
    const diPlusLine = adxChart.addSeries(LineSeries, {
      color: COLOR_DI_PLUS, lineWidth: 1,
      priceLineVisible: false, lastValueVisible: true, title: '+DI',
    });
    const diMinusLine = adxChart.addSeries(LineSeries, {
      color: COLOR_DI_MINUS, lineWidth: 1,
      priceLineVisible: false, lastValueVisible: true, title: '-DI',
    });
    const spreadLine = adxChart.addSeries(LineSeries, {
      color: COLOR_SPREAD, lineWidth: 1, lineStyle: 2,
      priceLineVisible: false, lastValueVisible: true, title: 'Spread',
    });
    const level25Line = adxChart.addSeries(LineSeries, {
      color: COLOR_LEVEL25, lineWidth: 1, lineStyle: 2,
      priceLineVisible: false, lastValueVisible: false, title: '25',
    });

    priceChartRef.current = priceChart;
    adxChartRef.current   = adxChart;
    candleRef.current     = candleSeries;
    seriesMarkersRef.current = createSeriesMarkers(candleSeries, []);
    adxLineRef.current    = adxLine;
    diPlusRef.current     = diPlusLine;
    diMinusRef.current    = diMinusLine;
    spreadRef.current     = spreadLine;
    level25Ref.current    = level25Line;

    let syncP = false;
    let syncA = false;
    const syncTimeRange = (target, range) => {
      if (syncBlockRef.current || !isValidVisibleRange(range)) return;
      syncBlockRef.current = true;
      try {
        target.timeScale().setVisibleRange(range);
      } catch (_) {
        /* ignore sync glitches during resize / empty series */
      } finally {
        syncBlockRef.current = false;
      }
    };
    priceChart.timeScale().subscribeVisibleTimeRangeChange(range => {
      if (syncA || syncBlockRef.current) return;
      syncP = true;
      syncTimeRange(adxChart, range);
      syncP = false;
    });
    adxChart.timeScale().subscribeVisibleTimeRangeChange(range => {
      if (syncP || syncBlockRef.current) return;
      syncA = true;
      syncTimeRange(priceChart, range);
      syncA = false;
    });

    const roP = new ResizeObserver(entries => {
      for (const e of entries) priceChart.applyOptions({ width: e.contentRect.width, height: e.contentRect.height });
    });
    roP.observe(priceContainerRef.current);

    const roA = new ResizeObserver(entries => {
      for (const e of entries) adxChart.applyOptions({ width: e.contentRect.width, height: e.contentRect.height });
    });
    roA.observe(adxContainerRef.current);

    return () => {
      roP.disconnect();
      roA.disconnect();
      priceChart.remove();
      adxChart.remove();
      priceChartRef.current = adxChartRef.current = null;
      candleRef.current = adxLineRef.current = diPlusRef.current = diMinusRef.current = null;
      spreadRef.current = level25Ref.current = null;
      seriesMarkersRef.current = null;
    };
  }, []);

  useEffect(() => {
    fetch('/api/config/pairs')
      .then(r => r.json())
      .then(data => {
        setAvailablePairs(data.pairs || []);
        setPair(data.default || (data.pairs && data.pairs[0]) || '');
      })
      .catch(() => setError('Failed to load trading pairs.'));
  }, []);

  useEffect(() => {
    if (!pair || !candleRef.current || !adxLineRef.current) return;

    const cfg = TF_CONFIG[timeframe];
    const days = cfg.defaultDays;

    let cancelled = false;
    setPriceLoading(true);
    setAdxLoading(true);
    setError(null);

    let priceUrl;
    if (timeframe === 'daily') {
      priceUrl = `/api/ohlc-daily?pair=${encodeURIComponent(pair)}&days=${days}`;
    } else {
      const intervalHours = cfg.ohlcInterval / 60;
      const limit = Math.ceil(days * 24 / intervalHours);
      priceUrl = `/api/ohlc?pair=${encodeURIComponent(pair)}&limit=${limit}`;
    }

    const adxUrl =
      `/api/indicators/adx?pair=${encodeURIComponent(pair)}`
      + `&timeframe=${cfg.adxTf}&days=${days}&warmup=false`;

    const fetchAdx = fetch(adxUrl).then((r) => {
      if (!r.ok) {
        return r.json().then((body) => {
          throw new Error(body.error || `HTTP ${r.status}`);
        });
      }
      return r.json();
    });

    Promise.all([fetch(priceUrl).then(r => r.json()), fetchAdx])
      .then(([priceJson, adxJson]) => {
        if (cancelled) return;

        const raw = (priceJson.data || [])
          .map(row => {
            const time = ohlcChartTime(row.time, timeframe);
            const open = finiteNum(row.open);
            const high = finiteNum(row.high);
            const low = finiteNum(row.low);
            const close = finiteNum(row.close);
            if (time == null || open == null || high == null || low == null || close == null) {
              return null;
            }
            return { time, open, high, low, close };
          })
          .filter(Boolean);

        let candles;
        if (timeframe === '4h') {
          candles = dedupSort(resampleTo4H(raw));
        } else {
          candles = dedupSort(raw);
        }

        const rows = adxJson.data || [];
        adxRowsRef.current = rows;

        const pushLine = (target, time, value) => {
          const v = finiteNum(value);
          if (time != null && v != null) target.push({ time, value: v });
        };

        const adxData = [];
        const diPlusData = [];
        const diMinusData = [];
        const spreadData = [];

        rows.forEach(row => {
          const t = chartTimeFromIso(row.time, timeframe);
          pushLine(adxData, t, row.adx);
          pushLine(diPlusData, t, row.di_plus);
          pushLine(diMinusData, t, row.di_minus);
          pushLine(spreadData, t, row.di_spread);
        });

        const sortedAdx = dedupSort(adxData);

        if (candleRef.current) candleRef.current.setData(candles);
        if (adxLineRef.current) adxLineRef.current.setData(sortedAdx);
        if (diPlusRef.current) diPlusRef.current.setData(dedupSort(diPlusData));
        if (diMinusRef.current) diMinusRef.current.setData(dedupSort(diMinusData));
        if (spreadRef.current) spreadRef.current.setData(dedupSort(spreadData));
        if (level25Ref.current && sortedAdx.length > 0) {
          level25Ref.current.setData(sortedAdx.map(p => ({ time: p.time, value: 25 })));
        }

        setLatest(adxJson.latest || (rows.length > 0 ? rows[rows.length - 1] : null));
        seriesMarkersRef.current?.setMarkers(buildDiCrossMarkers(rows, timeframe));

        const timePoints = [
          ...candles.map(c => c.time),
          ...sortedAdx.map(p => p.time),
        ].filter(t => t != null);

        const applyViewport = () => {
          const priceChart = priceChartRef.current;
          const adxChart = adxChartRef.current;
          if (!priceChart || !adxChart) return;

          const fitBoth = () => {
            try {
              priceChart.timeScale().fitContent();
              adxChart.timeScale().fitContent();
            } catch (_) {
              /* chart not ready */
            }
          };

          syncBlockRef.current = true;
          try {
            if (timePoints.length >= 2) {
              const sorted = [...timePoints].sort(compareChartTime);
              const range = { from: sorted[0], to: sorted[sorted.length - 1] };
              if (isValidVisibleRange(range)) {
                priceChart.timeScale().setVisibleRange(range);
                adxChart.timeScale().setVisibleRange(range);
              } else {
                fitBoth();
              }
            } else {
              fitBoth();
            }
          } catch (_) {
            fitBoth();
          } finally {
            syncBlockRef.current = false;
          }
        };

        requestAnimationFrame(() => {
          requestAnimationFrame(applyViewport);
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load ADX dashboard data.');
      })
      .finally(() => {
        if (cancelled) return;
        setPriceLoading(false);
        setAdxLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [pair, timeframe]);

  const regime = regimeDisplay(latest?.adx_regime);
  const direction = latest?.adx_direction;
  const loading = priceLoading || adxLoading;
  const cfg = TF_CONFIG[timeframe];

  const spreadStr = latest?.di_spread != null
    ? `${latest.di_spread >= 0 ? '+' : ''}${latest.di_spread.toFixed(1)}`
    : '—';

  return (
    <div className="adx-dashboard">
      <div className="adx-controls">
        <label htmlFor="adx-pair-select" className="pair-label">Pair</label>
        <select
          id="adx-pair-select"
          value={pair}
          onChange={e => setPair(e.target.value)}
          className="pair-select"
          disabled={availablePairs.length === 0}
        >
          {availablePairs.map(p => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>

        <div className="adx-tf-group">
          {TIMEFRAMES.map(tf => (
            <button
              key={tf}
              className={`adx-tf-btn${timeframe === tf ? ' active' : ''}`}
              onClick={() => setTimeframe(tf)}
            >
              {TF_CONFIG[tf].label}
            </button>
          ))}
        </div>

        <span className="adx-range-badge">
          {cfg.defaultDays === 1 ? '24H' : `${cfg.defaultDays}D`}
        </span>

        <div className="adx-divider" />

        <div className="adx-legend">
          <span className="adx-legend-item adx-color">
            ADX&nbsp;<strong>{latest?.adx != null ? latest.adx.toFixed(1) : '—'}</strong>
          </span>
          <span className={`adx-regime-badge ${regime.cls}`}>{regime.label}</span>
          <span className="adx-legend-item di-plus-color">
            +DI&nbsp;<strong>{latest?.di_plus != null ? latest.di_plus.toFixed(1) : '—'}</strong>
          </span>
          <span className="adx-legend-item di-minus-color">
            −DI&nbsp;<strong>{latest?.di_minus != null ? latest.di_minus.toFixed(1) : '—'}</strong>
          </span>
          <span className="adx-legend-item spread-color">
            Spread&nbsp;<strong>{spreadStr}</strong>
            {spreadTrendArrow(latest?.di_spread_slope_3)}
          </span>
          {direction && (
            <span className={`adx-direction-badge ${direction === 'bull' ? 'dir-bull' : 'dir-bear'}`}>
              {direction === 'bull' ? '▲ Bull' : '▼ Bear'}
            </span>
          )}
          {adxLoading && <span className="adx-loading-dot" />}
        </div>
      </div>

      <div className="adx-charts-area">
        {loading && (
          <div className="chart-overlay">
            <div className="spinner" />
            <span>Loading…</span>
          </div>
        )}
        {error && !loading && (
          <div className="chart-overlay error">
            <span>⚠️ {error}</span>
          </div>
        )}

        <div className="adx-price-wrapper">
          <div ref={priceContainerRef} className="adx-chart-container" />
        </div>

        <div className="adx-sub-wrapper">
          <div className="adx-sub-label">ADX / +DI / −DI / spread</div>
          <div ref={adxContainerRef} className="adx-chart-container" />
        </div>
      </div>
    </div>
  );
}

function resampleTo4H(candles1h) {
  const FOUR_HOURS = 4 * 3600;
  const buckets = new Map();

  candles1h.forEach(c => {
    const bucket = Math.floor(c.time / FOUR_HOURS) * FOUR_HOURS;
    if (!buckets.has(bucket)) {
      buckets.set(bucket, { time: bucket, open: c.open, high: c.high, low: c.low, close: c.close });
    } else {
      const b = buckets.get(bucket);
      b.high  = Math.max(b.high, c.high);
      b.low   = Math.min(b.low,  c.low);
      b.close = c.close;
    }
  });

  return Array.from(buckets.values());
}

export default AdxDashboard;
